<template>
  <li
    :class="[
      'markdown-message-content flex flex-col',
      index !== messageList.length - 1 && 'mb-20',
      item.role === 'user' ? 'items-end' : 'text-left',
      item.type,
      item.type === 'greeting' && 'shrink-0 pt-8',
    ]"
  >
    <div
      v-if="item.type === 'greeting' && hasHistoryMessage"
      class="text-12 text-#BDBDBD load-history-btn"
    >
      <div class="leading-17 font-600 mb-13 mt-10 text-center">
        {{ formatUTCTime("MM-DD HH:mm", item.date as any) }}
      </div>
    </div>

    <div
      v-if="item.thoughtContent"
      v-html="makeHtml(item.thoughtContent)"
      class="text-12 border-l-1 border-l-solid border-l-#e5e5e5 text-#8b8b8b m-0 pl-12 leading-[1.8] mb-10 [&_code]:whitespace-pre-wrap"
    ></div>

    <van-image
      class="max-w-2/3 rounded-14 mb-10"
      radius="14"
      fit="cover"
      :src="item.imageList"
      v-if="item.imageList"
      @click="showImagePreview({ images: item.imageList?.split(',') || [] })"
    />

    <div
      :class="[
        'rounded-14 inline-block px-10 py-2 text-14 font-600 leading-22 w-fit',
        item.role === 'user'
          ? ' bg-[linear-gradient(_180deg,#5D86FF_0%,_#1D31FF_100%)] text-white'
          : 'bg-#F7F7F7',
      ]"
    >
      <p
        class="leading-22 flex items-center gap-8"
        v-if="
          sendLoading &&
          item.role === 'assistant' &&
          index === messageList.length - 1
        "
      >
        {{ t("message.ai.thinking") }}
        <ThingLoading />
      </p>
      <div
        v-if="item.reasonContent"
        v-html="makeHtml(item.reasonContent)"
      ></div>
      <div v-if="item.audio && !item.reasonContent" class="text-center">
        <van-loading type="spinner" />
      </div>
    </div>

    <Topic
      v-if="item.type === 'greeting' && greetingWordList.length"
      :topics="greetingWordList"
      @item-click="(message) => emit('send-message', message)"
      @refresh="emit('refresh-greeting-word')"
    />
  </li>
  <li v-if="item.goodsList?.length" class="mt-10">
    <div
      class="bg-#F7F7F7 rounded-14 p-12 pb-2! gap-10 flex snap-x snap-mandatory overflow-x-auto [&>div]:w-[calc(100vw-16rem)] [&>div]:flex-[0_0_auto] [&>div]:snap-center [&>div]:snap-always"
    >
      <GoodsList
        v-if="item.goodsList.length > 0"
        :goodsData="item.goodsList.slice(0, 3)"
        :source="GoodsSource.Message"
      />
      <GoodsList
        v-if="item.goodsList.length > 3"
        :goodsData="item.goodsList.slice(3, 6)"
        :source="GoodsSource.Message"
      />
      <GoodsList
        v-if="item.goodsList.length > 6"
        :goodsData="item.goodsList.slice(6, 9)"
        :source="GoodsSource.Message"
      />
    </div>
    <div
      class="w-fit py-6 mb-10 px-11 rounded-21 border-1 border-solid border-#006EFF text-#006EFF text-14 font-600 mt-12"
      @click="
        showSuggestModal = true;
        suggestIndex = index;
      "
    >
      {{ t("message.ai.suggestMore") }}
    </div>
  </li>
  <div
    v-if="
      item.type !== 'greeting' &&
      item.role === 'assistant' &&
      index === messageList.length - 1 &&
      !replyLoading &&
      !sendLoading
    "
    class="flex items-center gap-16 [&_img]:size-16 my-10 py-4 px-10"
  >
    <img
      :src="RefreshImg"
      class="size-20"
      :class="[refreshLoading && 'animate-spin']"
      @click="!regenerateDisabled && handleRegenerate()"
    />
    <img
      :src="like ? ThumbUpActiveImg : ThumbUpImg"
      class="size-20"
      @click="handleOpt(like ? OptType.LikeCancel : OptType.Like)"
    />
    <img
      :src="dislike ? ThumbDownActiveImg : ThumbDownImg"
      class="size-20"
      @click="handleOpt(dislike ? OptType.DisLikeCancel : OptType.DisLike)"
    />
    <img :src="CopyImg" class="size-20" @click="handleCopy" />
  </div>
  <SuggestModal
    v-model:show="showSuggestModal"
    :message="messageList[suggestIndex]"
  />
</template>

<script setup lang="ts">
import { formatUTCTime } from "@/utils/time";
import { Message } from "./type";
import { makeHtml } from "./utils/makeHtml";
import RefreshImg from "@/assets/images/ai-chat/refresh.png";
import ThumbUpImg from "@/assets/images/ai-chat/thumb-up.png";
import ThumbDownImg from "@/assets/images/ai-chat/thumb-down.png";
import ThumbUpActiveImg from "@/assets/images/ai-chat/thumb-up-active.png";
import ThumbDownActiveImg from "@/assets/images/ai-chat/thumb-down-active.png";
import CopyImg from "@/assets/images/ai-chat/copy.png";
import { messageOpt, OptType } from "@/api/aiChat";
import { useSetting } from "@/composables/use-setting";
import { ref, toRaw } from "vue";
import { copyToClickBoard } from "./utils/copyToClickBoard";
import { showToast, showImagePreview, showSuccessToast } from "vant";
import Topic from "./Topic.vue";
import GoodsList from "./GoodsList.vue";
import SuggestModal from "./SuggestModal.vue";
import db from "./utils/db";
import ThingLoading from "./ThingLoading.vue";
import { useI18n } from "vue-i18n";
import { GoodsSource } from "./utils/goods-source";

const { t } = useI18n();

const regenerateDisabled = ref(false);

const props = defineProps<{
  item: Message;
  index: number;
  messageList: Message[];
  hasHistoryMessage: boolean;
  sendLoading: boolean;
  replyLoading: boolean;
  greetingWordList: string[];
}>();

const emit = defineEmits<{
  (e: "send-message", message: Partial<Message>): void;
  (e: "regenerate-message", id: number, resolve: () => void): void;
  (e: "refresh-greeting-word"): void;
}>();

const { getClientInfo } = useSetting();

const showSuggestModal = ref(false);
const suggestIndex = ref(0);
const refreshLoading = ref(false);
const likeLoading = ref(false);

const handleRegenerate = async () => {
  if (props.sendLoading || refreshLoading.value) return;
  regenerateDisabled.value = true;
  like.value = false;
  dislike.value = false;
  refreshLoading.value = true;
  await handleOpt(OptType.ReGenerate);
  refreshLoading.value = false;

  const result = new Promise<void>((resolve) =>
    emit("regenerate-message", props.item.id, resolve),
  );

  result.then(() => {
    regenerateDisabled.value = false;
  });
};

const like = ref(props.item.like);
const dislike = ref(props.item.dislike);

const handleOpt = async (type: OptType) => {
  if (likeLoading.value) return;
  const oldLike = toRaw(like.value);
  const oldDisLike = toRaw(dislike.value);
  const deviceCode = getClientInfo().deviceCode;
  if (type === OptType.Like || type === OptType.LikeCancel) {
    if (!like.value) {
      dislike.value = false;
    }
    like.value = !like.value;
  } else if (type === OptType.DisLike || type === OptType.DisLikeCancel) {
    if (!dislike.value) {
      like.value = false;
    }
    dislike.value = !dislike.value;
  }
  likeLoading.value = true;
  try {
    const res = await messageOpt({
      requestId: props.item.requestId!,
      sessionId: props.item.sessionId!,
      deviceCode,
      opt: type,
    });
    const json = await res.json();
    likeLoading.value = false;
    if (json && type) {
      if (
        type === OptType.Like ||
        type === OptType.DisLike ||
        type === OptType.DisLikeCancel ||
        type === OptType.LikeCancel
      ) {
        showSuccessToast({
          message: t("message.ai.feedbackSubmitted"),
          className: "w-fit!",
        });
      }
      const item = await db.messageList.get(props.item.id);
      if (item) {
        await db.messageList.put({
          ...item,
          like: like.value,
          dislike: dislike.value,
        });
      }
    }
  } catch (e) {
    console.log("Error", e);
    showToast(t("message.ai.serverError"));
    like.value = oldLike;
    dislike.value = oldDisLike;
  } finally {
    likeLoading.value = false;
    refreshLoading.value = false;
  }
};

const handleCopy = () => {
  copyToClickBoard(props.item.reasonContent);
  showToast(t("message.ai.copied"));
};
</script>
